class GiftCardForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('button[type="submit"]');
    
    // Get translations from data attributes
    this.translations = {
      fieldRequired: this.dataset.translationFieldRequired || 'This field is required',
      invalidEmail: this.dataset.translationInvalidEmail || 'Please enter a valid email address',
      pastDate: this.dataset.translationPastDate || 'Date cannot be in the past',
      selectAmount: this.dataset.translationSelectAmount || 'Please select an amount',
      success: this.dataset.translationSuccess || 'Gift card added to cart successfully!',
      errorGeneric: this.dataset.translationErrorGeneric || 'An error occurred. Please try again.',
      noProduct: this.dataset.translationNoProduct || 'No gift card product selected. Please configure a gift card product in the theme customizer.'
    };
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    
    // Handle amount selection to update variant ID
    const amountSelect = this.querySelector('#amount');
    if (amountSelect) {
      amountSelect.addEventListener('change', (event) => {
        const selectedOption = event.target.selectedOptions[0];
        const amount = selectedOption.value;
        const variantIdField = this.querySelector('#variant-id');
        const denominationField = this.querySelector('#denomination-value');
        
        console.log('Gift card amount selected:', amount);
        
        if (amount) {
          // Check if this is a variant ID (multi-variant gift card product)
          // Variant IDs are typically large numbers, amount values are decimal strings
          if (selectedOption.hasAttribute('data-price') && !amount.includes('.')) {
            // This is a variant ID from a multi-variant gift card product
            variantIdField.value = amount;
            denominationField.value = ''; // Clear denomination when using variants
            console.log('Using variant ID:', amount);
          } else {
            // This is an amount value for single-variant gift card with denomination property
            const formattedAmount = `$${parseFloat(amount).toFixed(2)}`;
            denominationField.value = formattedAmount;
            console.log('Using denomination property:', formattedAmount);
          }
        } else {
          // Clear both fields if no amount selected
          denominationField.value = '';
        }
      });
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    this.submitButton.setAttribute('disabled', true);
    this.submitButton.classList.add('loading');

    const formData = new FormData(this.form);
    
    // Debug: Check if we have a valid variant ID
    const variantId = formData.get('id');
    console.log('Variant ID being submitted:', variantId);
    
    if (!variantId || variantId === '' || variantId === 'null') {
      this.handleError(new Error(this.translations.noProduct));
      this.submitButton.removeAttribute('disabled');
      this.submitButton.classList.remove('loading');
      return;
    }
    
    // Debug: Log all form data being sent
    console.log('Form data being submitted:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }
    
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData)
      });

      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Handle non-JSON responses
        const textResponse = await response.text();
        console.log('Non-JSON response:', textResponse);
        data = { message: textResponse };
      }
      
      console.log('Cart add response:', data);
      console.log('Response status:', response.status);
      console.log('Response OK:', response.ok);

      if (response.ok) {
        this.showSuccessMessage();
        this.updateCartUI();
        
        // Reset form
        this.form.reset();
      } else {
        // Handle Shopify API errors
        let errorMessage = 'Error adding gift card to cart';
        
        if (data && data.message) {
          errorMessage = data.message;
        } else if (data && data.description) {
          errorMessage = data.description;
        } else if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && data.errors) {
          // Handle validation errors
          errorMessage = Object.values(data.errors).flat().join(', ');
        }
        
        console.error('Cart add error:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Gift card form submission error:', error);
      this.handleError(error);
    } finally {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.classList.remove('loading');
    }
  }

  showSuccessMessage() {
    // Remove any existing messages
    const existingMessages = this.querySelectorAll('.form-success-message, .form-error-message');
    existingMessages.forEach(msg => msg.remove());
    
    // Show success message
    const successMessage = document.createElement('div');
    successMessage.classList.add('form-success-message');
    successMessage.textContent = this.translations.success;
    successMessage.style.cssText = `
      background: #d4edda;
      color: #155724;
      border: 1px solid #c3e6cb;
      padding: 1rem;
      margin-bottom: 1rem;
      border-radius: 4px;
    `;
    this.form.insertBefore(successMessage, this.form.firstChild);
    
    // Remove the message after 5 seconds
    setTimeout(() => {
      if (successMessage.parentNode) {
        successMessage.remove();
      }
    }, 5000);
  }

  updateCartUI() {
    // Update cart info using existing cart animation if available
    if (window.cartAnimation && typeof window.cartAnimation.updateCartInfo === 'function') {
      window.cartAnimation.updateCartInfo();
      window.cartAnimation.triggerAnimation(this.submitButton);
    } else {
      // Fallback cart update
      this.updateCartCounts();
    }
  }

  updateCartCounts() {
    fetch('/cart.json')
      .then(res => res.json())
      .then(cart => {
        console.log('Updated cart:', cart);
        
        // Update cart count displays
        document.querySelectorAll(".cart-count").forEach(el => {
          if (el.classList.contains("cart-count-drawer")) {
            el.innerHTML = `(${cart.item_count})`;
          } else {
            el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
          }
        });
        
        // Update mobile cart count
        const mobileCartCount = document.querySelector('.tas-header-mobile__cart-count');
        if (mobileCartCount) {
          mobileCartCount.textContent = cart.item_count;
        }
        
        // Update header cart count
        const cartCount = document.querySelector('.tas-header__cart-count');
        if (cartCount) {
          cartCount.textContent = `${cart.item_count} ITEMS`;
        }
        
        // Update cart total
        const cartTotal = document.querySelector('.tas-header__cart-total');
        if (cartTotal) {
          const formatMoney = (cents) => {
            return '$' + (cents / 100).toFixed(2);
          };
          cartTotal.textContent = formatMoney(cart.total_price);
        }
        
        // Update header-total-price custom element
        const headerTotalPrice = document.querySelector("header-total-price");
        if (headerTotalPrice && typeof headerTotalPrice.updateTotal === 'function') {
          headerTotalPrice.updateTotal(cart);
        }
      })
      .catch(error => {
        console.error('Error updating cart UI:', error);
      });
  }

  validateForm() {
    const requiredFields = this.form.querySelectorAll('[required]');
    let isValid = true;

    // Clear previous errors
    this.form.querySelectorAll('.field-error').forEach(error => error.remove());
    this.form.querySelectorAll('.error').forEach(field => field.classList.remove('error'));

    requiredFields.forEach(field => {
      if (!field.value.trim()) {
        this.showFieldError(field, this.translations.fieldRequired);
        isValid = false;
      }
    });

    // Validate email format
    const emailField = this.form.querySelector('#recipient_email');
    if (emailField && emailField.value && !this.isValidEmail(emailField.value)) {
      this.showFieldError(emailField, this.translations.invalidEmail);
      isValid = false;
    }

    // Validate date - allow future dates for delayed delivery
    const dateField = this.form.querySelector('#send_date');
    if (dateField && dateField.value) {
      // Use the date string directly to avoid timezone conversion issues
      const selectedDateString = dateField.value; // This is already in YYYY-MM-DD format
      const todayString = new Date().toISOString().split('T')[0]; // Get today in YYYY-MM-DD format
      
      console.log('Date validation:');
      console.log('Selected date string:', selectedDateString);
      console.log('Today string:', todayString);

      if (selectedDateString < todayString) {
        this.showFieldError(dateField, this.translations.pastDate);
        isValid = false;
      }
      // Removed the 90-day client-side validation since Shopify's calculation differs
      // Let Shopify handle the 90-day validation server-side
    }

    // Validate amount selection
    const amountField = this.form.querySelector('#amount');
    if (amountField && !amountField.value) {
      this.showFieldError(amountField, this.translations.selectAmount);
      isValid = false;
    }

    return isValid;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  showFieldError(field, message) {
    field.classList.add('error');
    
    const errorDiv = document.createElement('div');
    errorDiv.classList.add('field-error');
    errorDiv.textContent = message;
    errorDiv.style.cssText = `
      color: #dc3545;
      font-size: 0.875rem;
      margin-top: 0.25rem;
    `;
    
    field.parentNode.insertBefore(errorDiv, field.nextSibling);
  }

  handleError(error) {
    // Remove any existing messages
    const existingMessages = this.querySelectorAll('.form-success-message, .form-error-message');
    existingMessages.forEach(msg => msg.remove());
    
    // Create error message container
    const errorContainer = document.createElement('div');
    errorContainer.classList.add('form-error-message');
    
    // Ensure we have a string message
    let errorMessage = this.translations.errorGeneric;
    if (error && error.message && typeof error.message === 'string') {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    errorContainer.textContent = errorMessage;
    errorContainer.style.cssText = `
      background: #f8d7da;
      color: #721c24;
      border: 1px solid #f5c6cb;
      padding: 1rem;
      margin-bottom: 1rem;
      border-radius: 4px;
    `;
    
    this.form.insertBefore(errorContainer, this.form.firstChild);

    // Scroll to error message
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Auto-remove error after 10 seconds
    setTimeout(() => {
      if (errorContainer.parentNode) {
        errorContainer.remove();
      }
    }, 10000);
  }
}

customElements.define('gift-card-form', GiftCardForm); 